import { reactive } from 'vue'
import apiOpenUseCase from '@/usecase/apiOpenUseCase'

export const POSITION_CACHE_KEY = 'roster_position_ids'
export const POSITION_CACHE_TTL = 60 * 60 * 1000 // 1 hour

export interface IRosterPositionIds {
  conductor: string
  driver: string
  helper: string
}

interface IPositionItem {
  id?: string
  code?: string
  name?: string
}

export const DEFAULT_SAFETY_POSITION_IDS: IRosterPositionIds = {
  conductor: '0197ce4c-8b56-72d6-8a2f-846489889347',
  driver: '01a11ec0-3e68-7d0b-adef-2d4b5c0ee725',
  helper: '01a11ec1-0d65-735e-b741-62421d775411'
}

const positionIds = reactive<IRosterPositionIds>({
  ...DEFAULT_SAFETY_POSITION_IDS
})

let positionLoadingPromise: Promise<void> | null = null

export async function resolvePositionId(code: string, fallbackName: string, safetyId: string): Promise<string> {
  try {
    const res = await apiOpenUseCase.get('/talent/v1/positions', `?code=${code}`)
    const codeList = res?.result?.data ?? []
    const matchedByCode = codeList.find((item: IPositionItem) => item.code === code)
    if (matchedByCode && matchedByCode.id) {
      return matchedByCode.id
    }
  } catch (e) {
    // fallback to name query
  }

  try {
    const res = await apiOpenUseCase.get('/talent/v1/positions', `?name=${encodeURIComponent(fallbackName)}`)
    const nameList = res?.result?.data ?? []
    const matchedByName = nameList.find((item: IPositionItem) => item.name?.toLowerCase() === fallbackName.toLowerCase())
    if (matchedByName && matchedByName.id) {
      return matchedByName.id
    }
  } catch (e) {
    // fallback to safetyId
  }

  return safetyId
}

export function clearPositionCache(): void {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(POSITION_CACHE_KEY)
    }
  } catch (e) {
    // ignore sessionStorage access errors
  }
}

export async function loadPositionIds(forceRefresh = false): Promise<void> {
  if (!forceRefresh) {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const cached = window.sessionStorage.getItem(POSITION_CACHE_KEY)
        if (cached) {
          const parsed = JSON.parse(cached)
          if (parsed && typeof parsed.expiry === 'number' && Date.now() < parsed.expiry && parsed.data) {
            positionIds.conductor = parsed.data.conductor || positionIds.conductor
            positionIds.driver = parsed.data.driver || positionIds.driver
            positionIds.helper = parsed.data.helper || positionIds.helper
            return
          }
        }
      }
    } catch (e) {
      // ignore storage reading/parsing error
    }
  }

  if (positionLoadingPromise) {
    await positionLoadingPromise
    return
  }

  positionLoadingPromise = (async () => {
    try {
      const [conductorId, driverId, helperId] = await Promise.all([
        resolvePositionId('B', 'Kondektur', DEFAULT_SAFETY_POSITION_IDS.conductor),
        resolvePositionId('A', 'Pengemudi', DEFAULT_SAFETY_POSITION_IDS.driver),
        resolvePositionId('H', 'Helper', DEFAULT_SAFETY_POSITION_IDS.helper)
      ])

      positionIds.conductor = conductorId
      positionIds.driver = driverId
      positionIds.helper = helperId

      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          const payload = {
            expiry: Date.now() + POSITION_CACHE_TTL,
            data: {
              conductor: positionIds.conductor,
              driver: positionIds.driver,
              helper: positionIds.helper
            }
          }
          window.sessionStorage.setItem(POSITION_CACHE_KEY, JSON.stringify(payload))
        }
      } catch (e) {
        // ignore storage writing error
      }
    } finally {
      positionLoadingPromise = null
    }
  })()

  await positionLoadingPromise
}

export function useRosterPositions() {
  return {
    positionIds,
    loadPositionIds,
    clearPositionCache,
    POSITION_CACHE_KEY
  }
}
